import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { routes } from 'constants/config';
import { Icon } from 'ui';
import {
  Tab,
  TabbedView,
  TabList,
  TabPanel,
} from 'components/shared/TabbedView';
import useFetch from 'components/shared/hooks/useFetch';
import { hasFeatureFlags } from 'utils/features';
import Select from 'components/shared/select';
import GradingStationTable from './table';

const GradingStationTableContainer = props => {
  const enabledMultipleReviewers = hasFeatureFlags([
    'readiness-enable-multiple-reviewers',
  ]);

  const {
    page,
    gradableType,
    query,
    rowsPerPage,
    params,
    gradableTypeName,
    gradableTypes,
  } = props;
  const currentTab = enabledMultipleReviewers
    ? params.get('tabName')
    : 'Lessons';
  const [tempQuery, setTempQuery] = useState(decodeURIComponent(query));
  const [tab, setTab] = useState(currentTab || 'Lessons');

  const isShowReviewingLessons =
    enabledMultipleReviewers && tab === 'AvailableReviewLessons';
  const TABS_ARRAY = enabledMultipleReviewers
    ? ['Lessons', 'AvailableReviewLessons']
    : ['Lessons'];

  const formatUrl = () => {
    if (gradableType === 'content') {
      if (tab === 'Lessons') {
        return routes.GRADES.LESSONS_LIST(page, rowsPerPage, query);
      }
      return routes.GRADES.LESSONS_LIST(page, rowsPerPage, query, true);
    }
    if (gradableType === 'user') {
      return routes.GRADES.USERS_LIST(page, rowsPerPage, query);
    }
    if (gradableType === 'long-waiting') {
      return routes.GRADES.LONG_WAITING_LIST(page, rowsPerPage, query);
    }
    if (gradableType === 'group') {
      return routes.GRADES.GROUPS_LIST(page, rowsPerPage, query);
    }
    return '';
  };

  const { data = {}, loading, error } = useFetch(formatUrl());

  if (error) {
    throw new Error('Error fetching content for grading station table.');
  }

  const itemsToGrade =
    data.lessons || data.users || data.progresses || data.groups || [];
  const [pageCount, setPageCount] = useState(
    itemsToGrade.length < rowsPerPage ? page : page + 1,
  );

  useEffect(() => {
    setPageCount(count => {
      if (data.totalPages) {
        return data.totalPages;
      }

      if (
        (itemsToGrade.length < rowsPerPage && page === count - 1) ||
        page === count
      ) {
        return itemsToGrade.length < rowsPerPage ? page : page + 1;
      }

      return count;
    });
  }, [itemsToGrade.length, rowsPerPage, data.totalPages, gradableType, page]);

  const handleTypeChange = ({ target }) => {
    params.set('gradable_type', target.value);
    params.delete('query');
    params.delete('page');
    params.delete('tabName');
    setTempQuery('');
    props.history.replace({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleQueryChange = ({ target }) => {
    if (target.value) {
      setTempQuery(target.value);
    } else {
      setTempQuery('');
    }
  };

  const handleQuerySubmit = () => {
    if (tempQuery) {
      params.set('query', encodeURIComponent(tempQuery));
    } else {
      params.delete('query');
    }
    params.delete('page');
    props.history.replace({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleClearQuery = () => {
    setTempQuery('');
    params.delete('query');
    params.delete('page');
    props.history.replace({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handlePageChange = value => {
    if (value > pageCount) {
      setPageCount(value);
    }
    params.set('page', value);
    props.history.replace({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleRowsPerPageChange = ({ target }) => {
    params.set('perPage', target.value);
    params.delete('page');
    props.history.replace({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleTabChange = index => {
    if (enabledMultipleReviewers) {
      params.set('tabName', TABS_ARRAY[index]);
      params.delete('query');
      params.delete('page');
      params.delete('perPage');
      setTempQuery('');
      props.history.replace({
        pathname: props.location.pathname,
        search: params.toString(),
      });
    }
    setTab(TABS_ARRAY[index]);
  };

  const renderSearch = () => (
    <div>
      <input
        className="h-36 ff-ss w-240 pl-12"
        placeholder={t('common.search')}
        onChange={e => handleQueryChange(e)}
        onKeyDown={e => {
          if (e.keyCode === 13 || e.key === 'Enter') handleQuerySubmit();
        }}
        value={tempQuery}
      />
      {query && (
        <Icon
          className="ml-neg-24 mr-10 c-gray-dark"
          name="close-circle"
          onClick={handleClearQuery}
        />
      )}
    </div>
  );
  const renderContentTabs = () => {
    return (
      <TabList
        className="d-f ai-c mb-16"
        tabClassName="border-navy-dark pl-24 pr-24"
      >
        {enabledMultipleReviewers && (
          <Tab>{t('grades.tabs.lessons_ungraded')}</Tab>
        )}
        {enabledMultipleReviewers && (
          <Tab>{t('grades.tabs.lessons_available_to_review')}</Tab>
        )}
        {!enabledMultipleReviewers && <Tab>{t('grades.tabs.lessons')}</Tab>}
        <span className="ml-a">{renderSearch()}</span>
      </TabList>
    );
  };
  return (
    <div>
      <div className="d-f jc-sb mt-24">
        {!isShowReviewingLessons && (
          <Select
            name="gradable_type"
            id="filter_by_gradable_type"
            value={gradableType}
            onChange={handleTypeChange}
            className="mr-8 mb-16"
          >
            {gradableTypes.map(type => (
              <option key={type} value={type}>
                {gradableTypeName(type)}
              </option>
            ))}
          </Select>
        )}
        {isShowReviewingLessons && (
          <div style={{ marginTop: -24 }} className="h-60 mr-8 mb-16">
            <span className="c-brand-gray-darker ff-ss fs-n fz-16 lh-32">
              {t('grades.description_time_limit_multiple_reviewer')}
            </span>
          </div>
        )}
        {gradableType !== 'content' && renderSearch()}
      </div>
      {gradableType === 'content' ? (
        <TabbedView
          selectedIndex={TABS_ARRAY.indexOf(tab)}
          onSelect={index => handleTabChange(index)}
        >
          {renderContentTabs()}
          <TabPanel>
            <GradingStationTable
              itemsToGrade={itemsToGrade}
              page={page}
              handlePageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={handleRowsPerPageChange}
              gradableType="lesson"
              pageCount={pageCount}
              loading={loading}
              query={query}
            />
          </TabPanel>
          {enabledMultipleReviewers && (
            <TabPanel>
              <GradingStationTable
                itemsToGrade={itemsToGrade}
                page={page}
                handlePageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={handleRowsPerPageChange}
                gradableType="available_review_lesson"
                pageCount={pageCount}
                loading={loading}
                query={query}
              />
            </TabPanel>
          )}
        </TabbedView>
      ) : (
        <GradingStationTable
          itemsToGrade={itemsToGrade}
          page={page}
          handlePageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={handleRowsPerPageChange}
          history={props.history}
          gradableType={
            tab === 'Lessons' && gradableType === 'content'
              ? 'lesson'
              : gradableType
          }
          pageCount={pageCount}
          loading={loading}
          query={query}
        />
      )}
    </div>
  );
};

GradingStationTableContainer.propTypes = {
  page: PropTypes.number,
  gradableType: PropTypes.string,
  query: PropTypes.string,
  rowsPerPage: PropTypes.number,
  params: PropTypes.shape({
    set: PropTypes.func,
  }),
  gradableTypeName: PropTypes.func,
  gradableTypes: PropTypes.arrayOf(PropTypes.string),
};

export default GradingStationTableContainer;
